import { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
// Animated counter component for stat numbers
function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const motionVal = useMotionValue(0);
  const springVal = useSpring(motionVal, { damping: 30, stiffness: 100 });

  useEffect(() => {
    if (inView) motionVal.set(value);
  }, [inView, value, motionVal]);

  useEffect(() => {
    return springVal.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = Math.floor(latest) + suffix;
      }
    });
  }, [springVal, suffix]);

  return (
    <span className="font-serif font-semibold tracking-wider text-7xl" ref={ref}>
      0{suffix}
    </span>
  );
}
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function CamundaStats() {
  return (
    <section className="relative w-full lg:py-24 md:py-16 py-10 bg-[#ffffff] text-slate-900 overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[450px] w-[850px] rounded-full bg-slate-200/50 blur-[130px]" />
      <div className="mx-auto 3xl:w-4/5 lg:w-11/12  px-6 md:px-12 relative z-10">
        <div className="flex flex-col items-center text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full backdrop-blur-md shadow-xs mb-3"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
              PROVEN PERFORMANCE
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#000036]"
          >
            Our{" "}
            <span className="italic font-serif font-normal text-[#5dc9f0]">
              Stats
            </span>{" "}
          </motion.h2>
        </div>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          <motion.div
            variants={itemVariants}
            className="group p-8 lg:p-12 flex flex-col justify-between items-center text-center md:border-r border-slate-300 hover:bg-white/50 transition-colors duration-300"
          >
            <div className="flex-1 flex items-center justify-center w-full py-6">
              <Counter value={99.9} suffix="%"/>
            </div>
            <p className="text-xs sm:text-base font-semibold capitalize text-slate-500 group-hover:text-slate-900 transition-colors">
              availability
            </p>
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="group p-8 xl:p-12 flex flex-col justify-between items-center text-center md:border-r  border-slate-300 hover:bg-white/50 transition-colors duration-300"
          >
            <div className="flex-1 flex items-center justify-center w-full py-6">
             <Counter value={33} suffix="%"/>
            </div>
            <p className="text-xs sm:text-base font-semibold capitalize text-slate-500 group-hover:text-slate-900 transition-colors">
              faster time-to-market
            </p>
          </motion.div>
          <motion.div
            variants={itemVariants}
            className="group p-8 xl:p-12 flex flex-col justify-between items-center text-center md:border-r lg:border-r-0 border-slate-300 hover:bg-white/50 transition-colors duration-300"
          >
            <div className="flex-1 flex items-center justify-center w-full py-6">
           <Counter value={30} suffix="%"/>
            </div>
            <p className="text-xs sm:text-base font-semibold capitalize text-slate-500 group-hover:text-slate-900 transition-colors">
              faster change cycles
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
